import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import PropTypes from "prop-types";
import "../../assets/scss/_exitmodal.scss";
import { exitConsultationUrl } from "../../utils/constants";
import useApis from "../../services/useAPI";
import Spinner from "react-bootstrap/Spinner";
import chevronDownIcon from "../../assets/icons/Chevron-down.svg";
import checkIcon from "../../assets/icons/Check-dark.svg";
import Notes from "../../stories/Forms/Notes";
import closeIcon from "../../assets/icons/Close.png";
import "../../assets/scss/_reject-modal.scss";

const REJECTION_REASONS = [
  { value: "clinically-unsuitable", label: "Clinically unsuitable" },
  {
    value: "consent-withdrawn-declined",
    label: "Consent declined or withdrawn",
  },
  {
    value: "unable-to-obtain-sample",
    label: "Unable to obtain sample",
  },
  {
    value: "test-failure",
    label: "Test failure",
  },
  { value: "no-show", label: "No show", disabled: true },
];

  const ExitModal = ({isOpen,onClose,onConfirm,bookingId,notes,onNotesChange, reasons = REJECTION_REASONS, notesRequiredFor = [], notesAlwaysRequired = false, reasonLabel = "Rejection Reason", showRequiredMark = true, confirmLabel = "Confirm", showCancel = true, onSubmit, className = "",}) => {
  const [selectedReason, setSelectedReason] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const {
    callApi,
    isLoading: isSubmitting,
  } = useApis();

  useEffect(() => {
    if (isOpen) {
      setSelectedReason("");
      setIsDropdownOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  if (!isOpen) return null;

  const selectedLabel =
    reasons.find((r) => r.value === selectedReason)?.label ||
    "Select a reason";

  const notesRequired =
    notesAlwaysRequired || notesRequiredFor.includes(selectedReason); 

  const handleOptionClick = (reason) => {
    if (reason.disabled) return;
    setSelectedReason(reason.value);
    setIsDropdownOpen(false);
  };

  const handleConfirmClick = async () => {
    if (onSubmit) {
      onSubmit({ reason: selectedReason, notes });
      return;
    }

    const payload = {
      booking_id: bookingId,
      rejection_reason: selectedReason,
      notes: notes || undefined,
    };
    const result = await callApi(payload, exitConsultationUrl, "POST");

    if (result?.success) {
      if (onConfirm) {
        onConfirm({
          success: true,
          booking_status: result.booking_status,
          message: result.message,
          rejection_reason: selectedReason,
        });
        navigate("/consultation-cancelled", {
          replace: true,
          state: {
            rejectionMessage: `Consultation rejected: ${result?.rejection_reason}`,
          },
        });
      }
      onClose();
    }
  };

  return (
    <div className="exit-consultation-overlay">
      <div
        className={`exit-consultation-modal ${className}`.trim()}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="exit-consultation-title"
      >
        <div className="exit-consultation-modal__header">
          <h2
            id="exit-consultation-title"
            className="exit-consultation-modal__title"
          >
            Exit consultation
          </h2>
          <button
            type="button"
            className="exit-consultation-modal__close"
            onClick={onClose}
            aria-label="Close"
            disabled={isSubmitting}
          >
            <img src={closeIcon} alt="" width="24" height="24" />
          </button>
        </div>

        <div
          className={`exit-consultation-modal__body${
            isDropdownOpen ? " exit-consultation-modal__body--dropdown-open" : ""
          }`}
        >
          <p className="exit-consultation-modal__subtitle">
            Are you sure you want to end this consultation?
          </p>
          {notesAlwaysRequired ? (
            <p className="exit-consultation-modal__description">
              The customer will be notified that we were unable to complete this service.
            </p>
          ) : (
            <p className="exit-consultation-modal__description">
              The customer will be notified that we were unable to complete and
              report their health check.
            </p>
          )}

          <div className="exit-consultation-modal__field" ref={dropdownRef}>
            <label
              id="rejection-reason-label"
              className="exit-consultation-modal__label"
            >
              {reasonLabel}{" "}
              {showRequiredMark && <span className="required">*</span>}
            </label>

            <div className="exit-consultation-modal__dropdown-wrapper">
              <button
                type="button"
                className={`exit-consultation-modal__dropdown${
                  isDropdownOpen
                    ? " exit-consultation-modal__dropdown--open"
                    : ""
                }`}
                aria-haspopup="listbox"
                aria-expanded={isDropdownOpen}
                aria-labelledby="rejection-reason-label"
                onClick={() => setIsDropdownOpen((prev) => !prev)}
                disabled={isSubmitting}
              >
                <span>{selectedLabel}</span>
                <span
                  className={`exit-consultation-modal__chevron${
                    isDropdownOpen
                      ? " exit-consultation-modal__chevron--open"
                      : ""
                  }`}
                >
                  <img src={chevronDownIcon} alt="" />
                </span>
              </button>

              {isDropdownOpen && (
                <ul
                  className="exit-consultation-modal__dropdown-panel"
                  role="listbox"
                  aria-labelledby="rejection-reason-label"
                >
                  {reasons.map((reason) => (
                    <li
                      key={reason.value}
                      role="option"
                      aria-selected={selectedReason === reason.value}
                      aria-disabled={reason.disabled || undefined}
                      className={`exit-consultation-modal__dropdown-option${
                        reason.disabled
                          ? " exit-consultation-modal__dropdown-option--disabled"
                          : ""
                      }${
                        selectedReason === reason.value
                          ? " exit-consultation-modal__dropdown-option--selected"
                          : ""
                      }`}
                      onClick={() => handleOptionClick(reason)}
                    >
                      <span>{reason.label}</span>
                      {selectedReason === reason.value && (
                        <img src={checkIcon} alt="" />
                        )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
            <div className="exit-consultation-modal__notes-wrapper">
               <Notes mandatory={notesRequired} value={notes} onChange={onNotesChange} />
            </div>
        </div>

        <div className="exit-consultation-modal__footer">
          {showCancel && (
            <button
              type="button"
              className="exit-consultation-modal__cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            className="exit-consultation-modal__confirm"
            onClick={handleConfirmClick}
            disabled={
              !selectedReason ||
              (notesRequired && !notes?.trim()) || 
              isSubmitting
            }
          >
            {isSubmitting ? (
              <Spinner animation="border" className="my-custom-spinner" />
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

ExitModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  bookingId: PropTypes.string.isRequired,
  reasons: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      disabled: PropTypes.bool,
    }),
  ),
  notesRequiredFor: PropTypes.arrayOf(PropTypes.string),
  notesAlwaysRequired: PropTypes.bool,
  reasonLabel: PropTypes.string,
  showRequiredMark: PropTypes.bool,
  confirmLabel: PropTypes.string,
  showCancel: PropTypes.bool,
  onSubmit: PropTypes.func,
};

ExitModal.defaultProps = {
  onConfirm: null,
};

export default ExitModal;